import {
  Button,
  formatDate,
  formatDateTime,
  formatMoney,
  formatPhone,
  ListView,
  RecordDrawer,
  useCursorList,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { RadioCards } from "@majitap/ui/arc/radio-cards/radio-cards";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import { type MatchTarget, paymentActions, type TillTransaction } from "../../payments";
import { type Payer, PayerPicker } from "../invoices/payer";
import styles from "./payments.module.css";

/** The unmatched queue, oldest money first, for the count on the view switch and the list. */
export const tillQueueQuery = queryOptions({
  queryKey: ["till", "unmatched"],
  queryFn: async () =>
    (await ok(api.GET("/v1/admin/payments/till", { params: { query: { status: "unmatched", limit: 100 } } }))).data,
});

/**
 * Till and paybill money (C2B), never matched on its own: staff check who paid and the account
 * they typed, then match it to an invoice, an order, or the payer's account.
 */
export type TillState = "unmatched" | "matched" | "duplicate" | "ignored";

const TILL_EMPTY: Record<TillState, { title: string; description: string }> = {
  unmatched: {
    title: "Nothing to match",
    description: "Money paid to the till or paybill waits here until it's matched.",
  },
  matched: { title: "Nothing matched yet", description: "Till payments you match show here." },
  duplicate: { title: "No duplicates", description: "Safaricom sending the same payment twice shows here, once." },
  ignored: { title: "Nothing ignored", description: "Till payments set aside without a match show here." },
};

export function TillView({ state }: { state: TillState }): ReactNode {
  const matched = state !== "unmatched";
  const navigate = useNavigate({ from: "/payments" });
  const format = useCompanyFormat();
  const [matching, setMatching] = useState<TillTransaction | null>(null);
  const list = useCursorList({
    queryKey: ["till", state, "list"],
    fetchPage: (cursor) =>
      ok(
        api.GET("/v1/admin/payments/till", {
          params: { query: { status: state, cursor, limit: 25 } },
        }),
      ),
  });
  return (
    <>
      <ListView
        caption={state === "unmatched" ? "Till payments to match" : `Till payments: ${state}`}
        itemName={{ one: "till payment", other: "till payments" }}
        rows={list.rows}
        rowKey={(t) => t.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={false}
        empty={TILL_EMPTY[state]}
        toolbar={
          <SegmentedControl
            label="Till payments"
            value={state}
            onValueChange={(v) =>
              navigate({ search: { view: "till", till: v === "unmatched" ? undefined : (v as TillState) } })
            }
            options={[
              { value: "unmatched", label: "To match" },
              { value: "matched", label: "Matched" },
              { value: "duplicate", label: "Duplicates" },
              { value: "ignored", label: "Ignored" },
            ]}
          />
        }
        columns={[
          {
            key: "occurred_at",
            label: "Received",
            render: (t) => <span className={styles.muted}>{formatDateTime(t.occurred_at, format)}</span>,
          },
          {
            key: "external_id",
            label: "M-Pesa receipt",
            render: (t) => <span className={styles.mono}>{t.external_id}</span>,
          },
          {
            key: "payer_name",
            label: "Payer",
            render: (t) => (
              <span className={styles.stack}>
                <span>{t.payer_name ?? "Unnamed"}</span>
                <span className={styles.muted}>{formatPhone(t.payer_phone) || "No phone"}</span>
              </span>
            ),
          },
          {
            key: "bill_reference",
            label: "Account typed",
            render: (t) => <span className={styles.mono}>{t.bill_reference || "—"}</span>,
          },
          {
            key: "amount",
            label: "Amount",
            numeric: true,
            render: (t) => formatMoney(t.amount, { ...format, currency: t.currency }),
          },
          {
            key: "action",
            label: "",
            render: (t) =>
              t.status === "unmatched" ? (
                <Button variant="secondary" size="sm" onClick={() => setMatching(t)}>
                  Match
                </Button>
              ) : t.payment_id ? (
                <Link to="/payments/$paymentId" params={{ paymentId: t.payment_id }} className={styles.link}>
                  The payment
                </Link>
              ) : null,
          },
        ]}
      />
      {matched ? null : (
        <p className={styles.muted}>
          Till and paybill money is never matched on its own. Check the account the payer typed, then match it to the
          invoice, order or payer.
        </p>
      )}
      <MatchDrawer till={matching} onClose={() => setMatching(null)} />
    </>
  );
}

/**
 * Match till money (`Console / Payments / Match`): find the payer, then pick one of their open
 * invoices, one of their unpaid orders, or their account.
 */
function MatchDrawer({ till, onClose }: { till: TillTransaction | null; onClose: () => void }) {
  const format = useCompanyFormat();
  return (
    <RecordDrawer
      open={till !== null}
      onOpenChange={(o) => !o && onClose()}
      title={till ? `Match ${formatMoney(till.amount, { ...format, currency: till.currency })}` : "Match"}
      description={
        till
          ? [
              till.external_id,
              till.payer_name,
              formatPhone(till.payer_phone),
              till.bill_reference ? `typed ${till.bill_reference}` : null,
            ]
              .filter(Boolean)
              .join(" · ")
          : undefined
      }
    >
      {till ? <MatchForm key={till.id} till={till} onDone={onClose} /> : null}
    </RecordDrawer>
  );
}

function MatchForm({ till, onDone }: { till: TillTransaction; onDone: () => void }) {
  const format = useCompanyFormat();
  const queryClient = useQueryClient();
  const [payer, setPayer] = useState<Payer | null>(null);
  const [choice, setChoice] = useState<string | null>(null);
  const owner = payer?.billee;
  const invoices = useQuery({
    queryKey: ["till-match", "invoices", owner],
    enabled: Boolean(owner),
    queryFn: async () =>
      (
        await ok(
          api.GET("/v1/admin/billing/invoices", { params: { query: { ...owner, standing: "open", limit: 25 } } }),
        )
      ).data,
  });
  const orders = useQuery({
    queryKey: ["till-match", "orders", owner],
    enabled: Boolean(owner),
    queryFn: async () =>
      (await ok(api.GET("/v1/admin/orders", { params: { query: { ...owner, limit: 25 } } }))).data.filter(
        (o) => o.status !== "cancelled" && (o.payment_status === "unpaid" || o.payment_status === "partial"),
      ),
  });
  const money = (v: string, currency: string) => formatMoney(v, { ...format, currency });
  const target = (): MatchTarget | null => {
    if (!choice || !owner) return null;
    const [kind, id] = choice.split(":");
    if (kind === "invoice") return { invoice_id: id };
    if (kind === "order") return { order_id: id };
    return owner;
  };
  const match = useMutation({
    mutationFn: () => paymentActions.match(till.id, target()!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["till"] });
      await queryClient.invalidateQueries({ queryKey: ["payments"] });
      await queryClient.invalidateQueries({ queryKey: ["invoices"] });
      onDone();
    },
  });
  const options = [
    ...(invoices.data ?? []).map((i) => ({
      value: `invoice:${i.id}`,
      label: `${i.invoice_number} · invoice`,
      description: `Owes ${money(i.amount_due, i.currency)}${i.due_at ? ` · due ${formatDate(i.due_at, format)}` : ""}`,
    })),
    ...(orders.data ?? []).map((o) => ({
      value: `order:${o.id}`,
      label: `${o.order_number} · order`,
      description: `${money(o.total_amount, format.currency)} · ${o.payment_status === "partial" ? "part paid" : "unpaid"}`,
    })),
    ...(payer
      ? [
          {
            value: "account",
            label: `${payer.label}'s account`,
            description: "Not for one bill: it lowers what they owe overall.",
          },
        ]
      : []),
  ];
  return (
    <div className={styles.form}>
      <PayerPicker
        value={payer}
        onChange={(p) => {
          setPayer(p);
          setChoice(null);
        }}
      />
      {payer ? (
        invoices.isPending || orders.isPending ? (
          <p className={styles.muted}>Loading their open invoices and orders…</p>
        ) : (
          <RadioCards aria-label="Match to" layout="list" value={choice} onValueChange={setChoice} options={options} />
        )
      ) : (
        <p className={styles.muted}>Find who paid: the name or number above, or the account they typed.</p>
      )}
      {choice?.startsWith("invoice:") ? (
        <p className={styles.muted}>
          It becomes a payment of the invoice. Anything beyond what's owed goes to their wallet.
        </p>
      ) : null}
      {match.error ? (
        <Alert tone="danger" title="Not matched">
          {messageFor(errorOf(match.error))}
        </Alert>
      ) : null}
      <div className={styles.footer}>
        <Button variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button disabled={!target()} loading={match.isPending} onClick={() => match.mutate()}>
          Match
        </Button>
      </div>
    </div>
  );
}
